/**
 * Care-Rep Ticket detail — drill-in with guarded resolution actions (commit 4/4).
 *
 * Read context (member, ticket meta, full transcript, contact log, audit) PLUS
 * the money-path controls: award points (one per ticket, ≤ TICKET_MAX_AWARD,
 * reason required), contact the member (writes a message + sends email), and move
 * the ticket through its lifecycle (valid transitions only). Awarding does NOT
 * move status (G5). Modelled on AdminRedemptionDetail.
 */
import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft } from 'lucide-react';
import {
  fetchTicketDetail, awardTicket, contactTicketMember, transitionTicket,
  type TicketStatus,
} from '../../lib/adminApi';

const TICKET_MAX_AWARD = 2000; // mirrors the server ceiling (G2); server re-enforces

// Display-only lifecycle (server re-validates via TICKET_TRANSITIONS) — controls
// which status buttons render for the current status.
const NEXT_STATUS: Record<TicketStatus, TicketStatus[]> = {
  open: ['contacted', 'resolved', 'closed'],
  contacted: ['resolved', 'closed'],
  resolved: ['closed'],
  closed: [],
};

const STATUS_BADGE: Record<TicketStatus, string> = {
  open: 'bg-amber-500/15 text-amber-300',
  contacted: 'bg-sky-500/15 text-sky-300',
  resolved: 'bg-emerald-500/15 text-emerald-300',
  closed: 'bg-slate-600/30 text-slate-400',
};

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="bg-slate-800 rounded-xl p-5 space-y-3">
      <h2 className="text-sm font-semibold text-slate-300 uppercase tracking-wide">{title}</h2>
      {children}
    </div>
  );
}

function Row({ k, v }: { k: string; v: string }) {
  return <div className="flex justify-between gap-4"><dt className="text-slate-500">{k}</dt><dd className="text-slate-200 text-right">{v}</dd></div>;
}

export default function AdminTicketDetail() {
  const { id = '' } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const [toast, setToast] = useState<string | null>(null);
  const [amount, setAmount] = useState('');
  const [awardReason, setAwardReason] = useState('');
  const [contactBody, setContactBody] = useState('');

  function showToast(msg: string) { setToast(msg); setTimeout(() => setToast(null), 4000); }
  function refetch() { void queryClient.invalidateQueries({ queryKey: ['admin', 'tickets'] }); }

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'tickets', 'detail', id],
    queryFn: () => fetchTicketDetail(id),
    enabled: !!id,
  });

  const awardM = useMutation({
    mutationFn: () => awardTicket(id, Number(amount), awardReason),
    onSuccess: (r) => { showToast(`Awarded ${r.awarded} pts — new balance ${r.new_balance}`); setAmount(''); setAwardReason(''); refetch(); },
    onError: (e: Error) => showToast(e.message),
  });
  const contactM = useMutation({
    mutationFn: () => contactTicketMember(id, contactBody),
    onSuccess: (r) => { showToast(r.email_sent ? 'Message sent' : 'Message saved — email failed to send'); setContactBody(''); refetch(); },
    onError: (e: Error) => showToast(e.message),
  });
  const transitionM = useMutation({
    mutationFn: (to: TicketStatus) => transitionTicket(id, to),
    onSuccess: (r) => { showToast(`Status → ${r.status}`); refetch(); },
    onError: (e: Error) => showToast(e.message),
  });

  if (isLoading) return <div className="text-slate-400 text-sm">Loading…</div>;
  if (!data) return <div className="text-slate-400 text-sm">Ticket not found.</div>;

  const { ticket, award } = data;
  const { member } = ticket;
  const amountNum = Number(amount);
  const amountValid = Number.isInteger(amountNum) && amountNum > 0 && amountNum <= TICKET_MAX_AWARD;
  const anyPending = awardM.isPending || contactM.isPending || transitionM.isPending;
  const nextStatuses = NEXT_STATUS[ticket.status];

  return (
    <div className="space-y-4">
      <Link to="/admin/care/tickets" className="inline-flex items-center gap-1 text-xs text-slate-400 hover:text-white">
        <ArrowLeft className="w-3 h-3" /> Back to tickets
      </Link>

      {/* Header */}
      <div className="bg-slate-800 rounded-xl p-6 space-y-3">
        <div className="flex items-center gap-2 flex-wrap">
          <h1 className="text-2xl font-bold font-display text-white">{ticket.issue_type}</h1>
          <span className={`text-xs px-2 py-0.5 rounded-full font-medium uppercase ${STATUS_BADGE[ticket.status]}`}>{ticket.status}</span>
          <span className="text-[10px] px-2 py-0.5 rounded-full font-medium uppercase bg-slate-700 text-slate-300">{ticket.source}</span>
        </div>
        <p className="text-sm text-slate-400">{member.email ?? member.display_name ?? ticket.member_id}</p>
        {ticket.summary && <p className="text-sm text-slate-300">{ticket.summary}</p>}

        {/* Status controls — only the valid next statuses for the current status */}
        <div className="flex items-center gap-2 flex-wrap pt-1">
          {nextStatuses.length === 0 ? (
            <span className="text-xs text-slate-500">No further status changes (closed).</span>
          ) : (
            nextStatuses.map((to) => (
              <button
                key={to}
                disabled={anyPending}
                onClick={() => {
                  if (to === 'closed' && !window.confirm('Close this ticket? This is terminal.')) return;
                  transitionM.mutate(to);
                }}
                className="px-3 py-1.5 rounded-lg text-xs font-medium bg-slate-700 text-slate-200 hover:bg-slate-600 disabled:opacity-40"
              >
                Mark {to}
              </button>
            ))
          )}
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {/* Award card (money path) */}
        <Card title="Award points">
          {award.awarded ? (
            <p className="text-sm text-emerald-300">Awarded {award.amount} pts. One award per ticket — this is locked.</p>
          ) : (
            <div className="space-y-2">
              <input
                type="number" min={1} max={TICKET_MAX_AWARD} value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder={`Amount (1–${TICKET_MAX_AWARD})`}
                aria-label="Award amount"
                className="w-full px-3 py-2 rounded-lg text-sm bg-slate-700 text-white border border-slate-600"
              />
              <textarea
                value={awardReason} onChange={(e) => setAwardReason(e.target.value)}
                placeholder="Reason (required)" aria-label="Award reason" rows={2}
                className="w-full px-3 py-2 rounded-lg text-sm bg-slate-700 text-white border border-slate-600"
              />
              <button
                onClick={() => awardM.mutate()}
                disabled={anyPending || !amountValid || awardReason.trim().length < 3}
                className="px-3 py-1.5 rounded-lg text-xs font-medium bg-emerald-900/50 text-emerald-300 hover:bg-emerald-900/70 disabled:opacity-40"
              >
                Award points
              </button>
              {amount && !amountValid && <p className="text-[11px] text-red-400">Amount must be 1–{TICKET_MAX_AWARD}.</p>}
            </div>
          )}
        </Card>

        {/* Contact card */}
        <Card title="Contact member">
          <div className="space-y-2">
            <textarea
              value={contactBody} onChange={(e) => setContactBody(e.target.value)}
              placeholder="Message to the member (emailed)" aria-label="Contact message" rows={3}
              className="w-full px-3 py-2 rounded-lg text-sm bg-slate-700 text-white border border-slate-600"
            />
            <button
              onClick={() => contactM.mutate()}
              disabled={anyPending || contactBody.trim().length === 0}
              className="px-3 py-1.5 rounded-lg text-xs font-medium bg-primary text-white hover:bg-primary/90 disabled:opacity-40"
            >
              Send
            </button>
          </div>
        </Card>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {/* Member */}
        <Card title="Member">
          <dl className="text-sm space-y-1.5">
            <Row k="Name" v={member.display_name ?? '—'} />
            <Row k="Email" v={member.email ?? '—'} />
            <Row k="Trust score" v={String(member.trust_score)} />
            <Row k="Balance" v={`${member.total_points.toLocaleString()} pts`} />
            <Row k="Account status" v={member.status} />
          </dl>
        </Card>

        {/* Ticket meta */}
        <Card title="Ticket">
          <dl className="text-sm space-y-1.5">
            <Row k="Source" v={ticket.source} />
            <Row k="Issue type" v={ticket.issue_type} />
            <Row k="Claim outcome" v={ticket.claim_outcome ?? '—'} />
            <Row k="Escalation reason" v={ticket.escalation_reason ?? '—'} />
            <Row k="Owed amount" v={ticket.owed_amount != null ? `${ticket.owed_amount} pts` : '—'} />
            <Row k="Assignee" v={ticket.assignee_id ?? 'Unassigned'} />
            <Row k="Opened" v={new Date(ticket.created_at).toLocaleString()} />
            <Row k="Updated" v={new Date(ticket.updated_at).toLocaleString()} />
            <Row k="Closed" v={ticket.closed_at ? new Date(ticket.closed_at).toLocaleString() : '—'} />
          </dl>
        </Card>
      </div>

      {/* Transcript */}
      <Card title="Transcript">
        {ticket.transcript && ticket.transcript.length ? (
          <div className="space-y-2">
            {ticket.transcript.map((turn, i) => (
              <div
                key={i}
                className={`rounded-lg px-3 py-2 text-sm max-w-[80%] ${
                  turn.role === 'user' ? 'bg-slate-700 text-slate-100' : 'bg-primary/15 text-primary-100 ml-auto'
                }`}
                data-role={turn.role}
              >
                <div className="text-[10px] uppercase tracking-wide text-slate-400 mb-0.5">
                  {turn.role === 'user' ? 'Member' : 'Assistant'}
                </div>
                {turn.content}
              </div>
            ))}
          </div>
        ) : (
          <p className="text-slate-400 text-sm">No transcript captured.</p>
        )}
      </Card>

      {/* Contact log */}
      <Card title="Contact log">
        {ticket.messages.length ? (
          <ul className="text-xs text-slate-300 space-y-2">
            {ticket.messages.map((m) => (
              <li key={m.id} className="border-l-2 border-slate-600 pl-3">
                <div className="text-slate-500">{m.direction} · {m.channel} · {new Date(m.created_at).toLocaleString()} · {m.actor_id}</div>
                {m.body}
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-slate-400 text-sm">No messages yet.</p>
        )}
      </Card>

      {/* Audit trail */}
      <Card title="Audit trail">
        {ticket.audits.length ? (
          <table className="w-full text-xs">
            <thead className="text-slate-500 uppercase text-left">
              <tr><th className="py-1 pr-3">When</th><th className="py-1 pr-3">Actor</th><th className="py-1 pr-3">Transition</th><th className="py-1">Reason</th></tr>
            </thead>
            <tbody>
              {ticket.audits.map((a) => (
                <tr key={a.id} className="border-t border-slate-700/60 text-slate-300">
                  <td className="py-1.5 pr-3 text-slate-400">{new Date(a.created_at).toLocaleString()}</td>
                  <td className="py-1.5 pr-3">{a.actor_id}</td>
                  <td className="py-1.5 pr-3">{a.from_status ?? '∅'} → {a.to_status}</td>
                  <td className="py-1.5">{a.reason ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p className="text-slate-400 text-sm">No history yet.</p>
        )}
      </Card>

      {toast && (
        <div className="fixed bottom-8 left-1/2 -translate-x-1/2 z-50 bg-slate-900 border border-slate-700 text-white text-sm font-medium px-5 py-3 rounded-xl shadow-lg">{toast}</div>
      )}
    </div>
  );
}
